import {
    AttributeList,
    AttributeKind,
    AttributeGroups,
} from 'types/attributes';
import { useLingui } from '@lingui/react';
import { GenreFamily, InstrumentFamily, SfxFamily } from 'types/enums';
import { Genres } from 'types/enums/genres';
import { Instruments } from 'types/enums/instruments';
import { Sfxs } from 'types/enums/sfx';
import { getMaxedAttributes } from './helpers/getMaxedAttributes';
import { mapToAttribute } from './helpers/mapToAttribute';
import { removeDuplicates } from './helpers/removeDuplicates';

export const useAttributesMapping = ({
    attributes,
    maxChar = 55,
}: {
    attributes: AttributeGroups;
    maxChar: number;
}) => {
    const { genres, instruments, sfxs, customs, tags } = attributes;
    const { i18n } = useLingui();

    const flattenAttributes: AttributeList = removeDuplicates([
        ...(genres?.map((genre) =>
            mapToAttribute({
                item: genre,
                kind: AttributeKind.Genre,
                validEnums: [GenreFamily, Genres],
                i18n,
            }),
        ) ?? []),
        ...(instruments?.map((instrument) =>
            mapToAttribute({
                item: instrument,
                kind: AttributeKind.Instrument,
                validEnums: [InstrumentFamily, Instruments],
                i18n,
            }),
        ) ?? []),
        ...(sfxs?.map((sfx) =>
            mapToAttribute({
                item: sfx,
                kind: AttributeKind.Sfx,
                validEnums: [SfxFamily, Sfxs],
                i18n,
            }),
        ) ?? []),
        ...(tags?.map((tag) =>
            mapToAttribute({
                item: tag,
                kind: AttributeKind.Tag,
                validEnums: [],
                i18n,
            }),
        ) ?? []),
        ...(customs?.map((custom) =>
            mapToAttribute({
                item: custom,
                kind: AttributeKind.Custom,
                validEnums: [],
                i18n,
            }),
        ) ?? []),
    ]);

    const maxedAttributes =
        maxChar > 0
            ? getMaxedAttributes(flattenAttributes, maxChar)
            : flattenAttributes;

    return maxedAttributes;
};
